import { MIN_LEVEL_BOOSTERS, newGame } from ".";
import cachedFirestore from "../cachedFirestore";
import {
  BOOSTER_CELLS_NAMES,
  BOOSTER_CELLS_NAMES_EXTERNAL,
  CELL_STATE,
} from "../cell";
import { PLATFORM } from "../config";
import { XLINKS } from "../constants";
import { throwErrorWithStack } from "../crashlytics";
import {
  boardEl,
  cellSelectorBackgroundEl,
  cellSelectorBlockedEl,
  cellSelectorCatEl,
  cellSelectorCatRmEl,
  cellSelectorCatchEl,
  cellSelectorCenterEl,
  cellSelectorDialogEl,
  cellSelectorDropEl,
  cellSelectorFreeEl,
  cellSelectorFreezeEl,
  cellSelectorNonBlockableEl,
  cellSelectorRmEl,
  cellSelectorShuffleEl,
  editorChangeShapeBtnEl,
  editorPendingTasksBtnEl,
  editorPendingTasksIconEl,
  editorPendingTasksTextEl,
  editorPlayBtnEl,
  editorPlayIconEl,
  editorRestartBtnEl,
  editorSaveBtnEl,
  editorSelectAllBtnEl,
  editorSelectAllDoneBtnEl,
  editorSelectPendingTasksBtnEl,
  editorShowLoosingFieldsBtnEl,
  editorZoomInBtnEl,
  editorZoomOutBtnEl,
  infoEl,
  infoTextEl,
  publishLevelAccessControlLabelPrivateEl,
  publishLevelAccessControlLabelPublicEl,
  publishLevelAccessControlRadioBtnEl,
  publishLevelDescriptionTextareaEl,
  publishLevelDialogCancelEl,
  publishLevelDialogEl,
  publishLevelDialogOkEl,
  publishLevelDialogScrollContainerEl,
} from "../elements";
import { addTouchEvent, removeAllTouchEvents } from "../events";
import { POSITIONS } from "../info";
import { hideInfo, showInfo } from "../info/modal";
import { SHAPE, field, lvlCfg, placement } from "../levels";
import { FirebaseStorage } from "../plugins";
import { showProfile } from "../profile";
import { convertSvg } from "../recordGame";
import { initRipple } from "../ripple";
import { showInfoSnackbar, showSuccessSnackbar } from "../snackbar";
import { playMp3 } from "../sound";
import { withSpinner } from "../spinner";
import store from "../store";
import {
  addScrollContainerFocusEventCallback,
  prettyTimeDeltaFormat,
} from "../utils";
import { validateInput } from "../validate";
import { gameEditorState, gameState, levelDef } from "./gameState";
import { updateStyles } from "../nav/styles";
import { yieldToMain } from "../promise";
import { stack } from "../nav";
import {
  autoTranslate,
  autoTranslateElements,
  resetLangAttributeOfLeafs,
} from "../translate";
import { getLogger } from "../logging";

const log = getLogger("game/editor");

function containsArray(arrays, target) {
  return arrays.some(
    (arr) =>
      arr.length === target.length &&
      arr.every((value, index) => value === target[index])
  );
}

const STATE_MAP = [
  CELL_STATE.FREE,
  CELL_STATE.NON_BLOCKABLE,
  CELL_STATE.BLOCKED,
  CELL_STATE.DROP,
  CELL_STATE.CENTER,
  CELL_STATE.SHUFFLE,
  CELL_STATE.CATCH,
  CELL_STATE.FREEZE,
  CELL_STATE.ERASE,
];

let gSelectedCoords = [];
let gElementsPlacedFieldSize = 0;
let gPlacements = {};
let gCatPlacements = {};
let gSelectorPlacements = {};
const gWonLevelDefs = new Set();
let gTimeout = null;

function resetMultiSelect() {
  gSelectedCoords = [];
  gSelectorPlacements = {};
}

export function initEditorPlacements() {
  for (let i = 0; i < levelDef.fieldSize; i++) {
    for (let j = 0; j < levelDef.fieldSize; j++) {
      const [xRel, yRel] = getRelCoords(i, j);
      const cell = gameState.cells[j][i];
      if (cell.state === CELL_STATE.NON_BLOCKABLE_OUTSIDE)
        cell.state = CELL_STATE.NON_BLOCKABLE;
      const coordStr = `${xRel},${yRel}`;
      if (gameState.cats.in(xRel, yRel)) gCatPlacements[coordStr] = true;
      if ([CELL_STATE.FREE, CELL_STATE.OUTSIDE].includes(cell.state)) continue;
      gPlacements[coordStr] = placement(cell.state, SHAPE.POINT, [xRel, yRel]);
    }
  }
  gElementsPlacedFieldSize = levelDef.fieldSize;
}

function resetNonBlockableOutsideCells(level) {
  for (const placement of level.placements)
    if (placement.state === CELL_STATE.NON_BLOCKABLE_OUTSIDE)
      placement.state = CELL_STATE.NON_BLOCKABLE;
}

function updateGame(level, options) {
  resetNonBlockableOutsideCells(level);
  newGame({ level, ...options });
}

function storeWonLevelDef(level) {
  if (store.step < 2) {
    showInfoSnackbar(
      "Level too easy to publish.\nWinning in less than 3 steps isn't challenging enough for publication."
    );
    return;
  }
  gWonLevelDefs.add(JSON.stringify(level));
  gameEditorState.editing = true;
  editorPlayIconEl.innerHTML = "play_arrow";
  editorPlayBtnEl.style.backgroundColor = "lightgreen";
  updateGame(levelDef);
  handleEditingState();
  handleEditorUpdate();
  updateStyles();
}

let gCatsTrappedCb = null;
let gGameEndedCb = null;

function newGameWithWonCallback(level, options) {
  updateGame(level, options);
  gCatsTrappedCb = () => storeWonLevelDef(level);
  gGameEndedCb = () => newGameWithWonCallback(level, options);
  addEventListener("catsTrapped", gCatsTrappedCb, { once: true });
  addEventListener("gameEnded", gGameEndedCb, {
    once: true,
  });
}

export function removeGameEventListeners() {
  if (gCatsTrappedCb) removeEventListener("catsTrapped", gCatsTrappedCb);
  if (gGameEndedCb) removeEventListener("gameEnded", gGameEndedCb);
}

function addTouchEvents(relativeCoords) {
  removeAllTouchEvents(cellSelectorCatEl);
  addTouchEvent(cellSelectorCatEl, () => {
    playMp3("click");
    const oldShapes = levelDef.catPlacement.shapes.map(([shape]) => shape);
    const oldShapeVariables = levelDef.catPlacement.shapes.map(
      ([, shapeVariable]) => shapeVariable
    );
    const relativeCoordsFiltered = relativeCoords
      .filter(([x, y, cell]) => {
        return (
          !containsArray(oldShapeVariables, [x, y]) &&
          !gCatPlacements[`${x},${y}`] &&
          !cell.win &&
          !cell.state == CELL_STATE.OUTSIDE
        );
      })
      .map(([x, y]) => [x, y]);
    relativeCoordsFiltered.forEach(([x, y]) => {
      gCatPlacements[`${x},${y}`] = true;
    });

    const level = lvlCfg({
      placements: levelDef.placements,
      catPlacement: placement(
        null,
        [
          ...oldShapes,
          ...Array(relativeCoordsFiltered.length).fill(SHAPE.POINT),
        ],
        [...oldShapeVariables, ...relativeCoordsFiltered]
      ),
      fieldGeometries: levelDef.fieldGeometries,
      fieldSize: levelDef.fieldSize,
    });
    updateGame(level);
    gElementsPlacedFieldSize = levelDef.fieldSize;
    resetMultiSelect();
    closeCellSelector();
    handleEditorUpdate();
  });
  removeAllTouchEvents(cellSelectorCatRmEl);
  addTouchEvent(cellSelectorCatRmEl, () => {
    playMp3("click");
    const oldShapes = levelDef.catPlacement.shapes.map(([shape]) => shape);
    const oldShapeVariables = levelDef.catPlacement.shapes.map(
      ([, shapeVariable]) => shapeVariable
    );

    const relativeCoordsFiltered = relativeCoords
      .filter(([x, y]) => containsArray(oldShapeVariables, [x, y]))
      .map(([x, y]) => [x, y]);

    relativeCoordsFiltered.forEach(([x, y]) => {
      delete gCatPlacements[`${x},${y}`];
    });

    const shapes = oldShapes.filter(
      (_, index) =>
        !containsArray(relativeCoordsFiltered, oldShapeVariables[index])
    );
    const shapeVariables = oldShapeVariables.filter(
      (_, index) =>
        !containsArray(relativeCoordsFiltered, oldShapeVariables[index])
    );

    const level = lvlCfg({
      placements: levelDef.placements,
      catPlacement: placement(null, shapes, shapeVariables),
      fieldGeometries: levelDef.fieldGeometries,
      fieldSize: levelDef.fieldSize,
    });
    updateGame(level);
    gElementsPlacedFieldSize = levelDef.fieldSize;
    resetMultiSelect();
    closeCellSelector();
    handleEditorUpdate();
  });

  [
    cellSelectorFreeEl,
    cellSelectorNonBlockableEl,
    cellSelectorBlockedEl,
    cellSelectorDropEl,
    cellSelectorCenterEl,
    cellSelectorShuffleEl,
    cellSelectorCatchEl,
    cellSelectorFreezeEl,
    cellSelectorRmEl,
  ].forEach((el, index) => {
    removeAllTouchEvents(el);
    const state = STATE_MAP[index];
    const minLevelIdx = MIN_LEVEL_BOOSTERS[state];

    const disabled = store.maxLevelIdx < minLevelIdx;
    if (minLevelIdx !== undefined && disabled) disableBooster(el);

    addTouchEvent(el, () => {
      playMp3("click");

      if (disabled) {
        return showInfo({
          el,
          position: POSITIONS.BOTTOM,
          text: `Unlock ${BOOSTER_CELLS_NAMES_EXTERNAL[state]} after completing Story Level ${minLevelIdx}.`,
        });
      }

      log.debug(
        `Setting state ${
          BOOSTER_CELLS_NAMES[state] || state
        } for ${relativeCoords}`
      );

      for (const [x, y, cell] of relativeCoords) {
        const coordStr = `${x},${y}`;
        if (gPlacements[coordStr]) cell.use.parentNode.removeChild(cell.use);
        if (state === CELL_STATE.FREE) delete gPlacements[coordStr];
        else gPlacements[coordStr] = placement(state, SHAPE.POINT, [x, y]);
      }

      const level = lvlCfg({
        catPlacement: levelDef.catPlacement,
        placements: Array.from(Object.values(gPlacements)),
        fieldGeometries: levelDef.fieldGeometries,
        fieldSize: levelDef.fieldSize,
      });
      updateGame(level);
      resetMultiSelect();
      closeCellSelector();
      gElementsPlacedFieldSize = levelDef.fieldSize;
      handleSelections();
      handleEditorUpdate();
    });
  });
}

function disableBooster(el) {
  if (el.getAttributeNS(null, "disabled")) return;
  const hrefPrev = el.getAttributeNS(XLINKS, "href");
  el.setAttributeNS(XLINKS, "href", `${hrefPrev}-disabled`);
  el.setAttributeNS(null, "disabled", true);
}

function handleSelections() {
  if (Object.keys(gSelectorPlacements).length) {
    editorSelectAllDoneBtnEl.classList.remove("disabled-icons-container");
  } else if (
    !editorSelectAllDoneBtnEl.classList.contains("disabled-icons-container")
  ) {
    editorSelectAllDoneBtnEl.classList.add("disabled-icons-container");
  }
}

function getRelCoords(x, y) {
  const halfFieldSizeFloor = Math.floor(levelDef.fieldSize / 2);
  const xRel = x - halfFieldSizeFloor;
  const yRel = y - halfFieldSizeFloor;
  return [xRel, yRel];
}

export function selectCell(use, x, y) {
  const [xRel, yRel] = getRelCoords(x, y);

  if (gameEditorState.selectionModeMultiple) {
    const coordsString = `${xRel},${yRel}`;
    if (gSelectorPlacements[coordsString]) {
      gSelectorPlacements[coordsString].parentNode.removeChild(
        gSelectorPlacements[coordsString]
      );
      delete gSelectorPlacements[coordsString];
      gSelectedCoords = gSelectedCoords.filter(
        ([x, y]) => `${x},${y}` !== coordsString
      );
      handleSelections();
      return;
    }
    const selectorUse = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "use"
    );
    selectorUse.setAttributeNS(null, "class", "cell");
    selectorUse.setAttributeNS(
      null,
      "transform",
      use.getAttributeNS(null, "transform")
    );
    selectorUse.setAttributeNS(XLINKS, "xlink:href", "#selector-cell");
    addTouchEvent(selectorUse, () => selectCell(use, x, y));

    const catEl = document.querySelector(".cat");
    if (catEl) catEl.parentNode.insertBefore(selectorUse, catEl);
    else boardEl.appendChild(selectorUse);

    gSelectedCoords.push([xRel, yRel, gameState.cells[y][x]]);
    gSelectorPlacements[coordsString] = selectorUse;
    handleSelections();
    return;
  }

  cellSelectorDialogEl.style.display = "initial";
  addTouchEvents([[xRel, yRel, gameState.cells[y][x]]]);
}

export function closeCellSelector() {
  cellSelectorDialogEl.style.display = "none";
}

export function handleEditingState() {
  if (gameEditorState.editing) {
    editorPlayIconEl.innerHTML = "play_arrow";
    editorPlayIconEl.nextElementSibling.textContent = "Start game";
    editorPlayBtnEl.style.backgroundColor = "lightgreen";
    editorShowLoosingFieldsBtnEl.classList.remove("disabled-icons-container");
    editorRestartBtnEl.classList.remove("disabled-icons-container");
    editorSelectAllBtnEl.classList.remove("disabled-icons-container");
    editorChangeShapeBtnEl.classList.remove("disabled-icons-container");
    editorZoomInBtnEl.classList.remove("disabled-icons-container");
    editorZoomOutBtnEl.classList.remove("disabled-icons-container");
    editorSelectAllDoneBtnEl.classList.remove("disabled-icons-container");
    editorSaveBtnEl.classList.remove("disabled-icons-container");
    editorSaveBtnEl.classList.add("stretch-zoom");
    editorSelectAllDoneBtnEl.style.visibility = "visible";
    updateGame(levelDef);
  } else {
    editorPlayIconEl.innerHTML = "stop";
    editorPlayIconEl.nextElementSibling.textContent = "Edit game";
    editorPlayBtnEl.style.backgroundColor = "orange";
    editorShowLoosingFieldsBtnEl.classList.add("disabled-icons-container");
    editorRestartBtnEl.classList.add("disabled-icons-container");
    editorSelectAllBtnEl.classList.add("disabled-icons-container");
    editorChangeShapeBtnEl.classList.add("disabled-icons-container");
    editorZoomInBtnEl.classList.add("disabled-icons-container");
    editorZoomOutBtnEl.classList.add("disabled-icons-container");
    editorSelectAllDoneBtnEl.classList.add("disabled-icons-container");
    editorSaveBtnEl.classList.add("disabled-icons-container");
    editorSaveBtnEl.classList.remove("stretch-zoom");
    editorSelectAllDoneBtnEl.style.visibility = "hidden";
    newGameWithWonCallback(levelDef);
  }
  resetLangAttributeOfLeafs(editorPlayIconEl.nextElementSibling);
}

addTouchEvent(cellSelectorBackgroundEl, closeCellSelector);
addTouchEvent(editorPlayBtnEl, () => {
  gameEditorState.editing = !gameEditorState.editing;
  handleEditingState();
  handleEditorUpdate();
});

function getFieldSizeBounds() {
  if (gameState.cats.num + Object.keys(gPlacements).length < 2)
    gElementsPlacedFieldSize = 0;
  const zoomLevelOne = (gElementsPlacedFieldSize + 1) % 4;
  return {
    min: gElementsPlacedFieldSize ? (zoomLevelOne ? 9 : 7) : 7,
    max: gElementsPlacedFieldSize ? (zoomLevelOne ? 17 : 19) : 19,
    step: gElementsPlacedFieldSize ? 4 : 2,
  };
}

addTouchEvent(editorZoomInBtnEl, () => {
  const { min, max, step } = getFieldSizeBounds();
  levelDef.fieldSize = Math.min(max, Math.max(min, levelDef.fieldSize - step));
  updateGame(levelDef);
  resetMultiSelect();
  handleSelections();
  handleEditorUpdate();
});
addTouchEvent(editorZoomOutBtnEl, () => {
  const { min, max, step } = getFieldSizeBounds();
  levelDef.fieldSize = Math.min(max, Math.max(min, levelDef.fieldSize + step));
  updateGame(levelDef);
  resetMultiSelect();
  closeCellSelector();
  handleSelections();
  handleEditorUpdate();
});

function restart() {
  newGame();
  gPlacements = {};
  gCatPlacements = {};
  resetMultiSelect();
  closeCellSelector();
  handleEditorUpdate();
  handleSelections();
}

addTouchEvent(editorRestartBtnEl, restart);

let _currentShapeIndex = 0;
addTouchEvent(editorChangeShapeBtnEl, () => {
  const availableShapes = [
    field(SHAPE.FULL),
    field(SHAPE.HEX_2),
    field(SHAPE.TRIANGLE),
    field(SHAPE.TRIANGLE_INV),
    // field([SHAPE.TRIANGLE_INV, SHAPE.TRIANGLE], [null, null]),
    // field([SHAPE.TRIANGLE_INV, SHAPE.TRIANGLE, SHAPE.HEX_2], [null, null, null]),
    // field([SHAPE.TRIANGLE_INV, SHAPE.HEX_2], [null, null]),
    // field([SHAPE.TRIANGLE, SHAPE.HEX_2], [null, null]),
  ];
  _currentShapeIndex = (_currentShapeIndex + 1) % availableShapes.length;
  levelDef.fieldGeometries = availableShapes[_currentShapeIndex];
  updateGame(levelDef);
  resetMultiSelect();
  closeCellSelector();
  handleSelections();
  handleEditorUpdate();
});

addTouchEvent(editorSelectAllBtnEl, () => {
  gameEditorState.selectionModeMultiple =
    !gameEditorState.selectionModeMultiple;
  editorSelectAllBtnEl.style.backgroundColor =
    gameEditorState.selectionModeMultiple ? "lightgreen" : "lightyellow";
  if (gameEditorState.selectionModeMultiple)
    editorSelectAllDoneBtnEl.style.display = "flex";
  else {
    editorSelectAllDoneBtnEl.style.display = null;
    Object.values(gSelectorPlacements).forEach((selectorPlacement) => {
      selectorPlacement.parentNode.removeChild(selectorPlacement);
    });
    resetMultiSelect();
  }
  handleSelections();
});
addTouchEvent(editorShowLoosingFieldsBtnEl, () => {
  gameEditorState.showLoosingFields = !gameEditorState.showLoosingFields;
  editorShowLoosingFieldsBtnEl.style.backgroundColor =
    gameEditorState.showLoosingFields ? "lightgreen" : "lightyellow";
  updateGame(levelDef);
  resetMultiSelect();
});
addTouchEvent(editorSelectAllDoneBtnEl, () => {
  addTouchEvents(gSelectedCoords);
  cellSelectorDialogEl.style.display = "initial";
});

const publish = async () => {
  const validInput = await checkInput();
  if (!validInput) return;
  const { path, id } = await createThumbnail();
  const { downloadUrl } = await withSpinner(
    FirebaseStorage.getDownloadUrl({
      path,
    })
  );
  resetNonBlockableOutsideCells(levelDef);
  const data = {
    levelDef: JSON.stringify(levelDef),
    creatorId: store.user.uid,
    creatorUname: store.profile.uname,
    creatorName: store.profile.name,
    creatorThumbnailUrl: store.profile.thumbnailUrl || store.user.photoUrl,
    isPublic: publicGlob,
    levelId: id,
    thumbnailUrl: downloadUrl,
    description: lvlDescriptionGlob,
    createdAt: new Date().getTime(),
    platform: PLATFORM,
    appVersion: store.appVersion,
  };
  await withSpinner(
    cachedFirestore.setDocument({
      reference: `communityLevels/${id}`,
      data,
      throwOnError: true,
    }),
    "Saving level..."
  );
  //   setLevelsCreatedCount((store.profile.levelsCreated || 0) + 1);
  cachedFirestore.clearCollectionCache("communityLevels");
  stack.pop();
  await withSpinner(showProfile());
  updateLast5PublishedLevelDates();
  restart();
  handleEditorUpdate();
  publishLevelDialogEl.style.display = null;
  store.set("levelEditing", {});
  showSuccessSnackbar("Level published!");
};

addTouchEvent(editorSaveBtnEl, () => {
  publishLevelDialogEl.style.display = "initial";
});

async function createThumbnail() {
  const svgEl = store.gameFrames[store.gameFrames.length - 1];
  const data = new XMLSerializer().serializeToString(svgEl);
  try {
    const resp = await withSpinner(
      convertSvg({
        functionName: "convert_svg_to_image",
        data,
        bucket: "community-levels",
      }),
      "Creating thumbnail..."
    );
    return resp.json();
  } catch (error) {
    throwErrorWithStack(error, true);
  }
}

let infoInnerHTMLGlob = "";
let tasksLeftGlob = 2;

export function handleEditorUpdate() {
  tasksLeftGlob = 0;
  infoInnerHTMLGlob = `<div style="margin-bottom: 1vmin;">`;
  if (gameState.cats && gameState.cats.num) {
    infoInnerHTMLGlob += `
        <span style="font-size:6vmin;vertical-align: bottom; margin-bottom: -0.6vmin; color: darkgreen;" class="material-symbols-outlined notranslate">
            check_box
        </span>
        <span style="text-decoration: line-through">
            Place at least one cat
        </span>`;
  } else {
    infoInnerHTMLGlob += `
        <span style="font-size:6vmin;vertical-align: bottom; margin-bottom: -0.6vmin;" class="material-symbols-outlined notranslate">
            check_box_outline_blank
        </span>
        Place at least one cat`;
    tasksLeftGlob += 1;
  }
  infoInnerHTMLGlob += `</div><div style="margin-bottom: 1vmin;">`;
  if (gWonLevelDefs.has(JSON.stringify(levelDef))) {
    infoInnerHTMLGlob += `
        <span style="font-size:6vmin;vertical-align: bottom; margin-bottom: -0.6vmin; color: darkgreen;" class="material-symbols-outlined notranslate">
            check_box
        </span>
        <span style="text-decoration: line-through">
            Play and win the level
        </span>`;
  } else {
    infoInnerHTMLGlob += `
        <span style="font-size:6vmin;vertical-align: bottom; margin-bottom: -0.6vmin;" class="material-symbols-outlined notranslate">
            check_box_outline_blank
        </span>
        Play and win the level`;
    tasksLeftGlob += 1;
  }
  infoInnerHTMLGlob += `</div><div>`;
  const waitingTime = getWaitingTime();
  if (waitingTime) {
    infoInnerHTMLGlob += `
        <span style="font-size:6vmin;vertical-align: bottom; margin-bottom: -0.6vmin;" class="material-symbols-outlined notranslate">
            check_box_outline_blank
        </span>
        Wait <span id="editor-waiting-time">${waitingTime}</span> `;
    if (!gTimeout) setWaitingTimeInterval();
    tasksLeftGlob += 1;
  } else {
    infoInnerHTMLGlob += `
        <span style="font-size:6vmin;vertical-align: bottom; margin-bottom: -0.6vmin; color: darkgreen;" class="material-symbols-outlined notranslate">
            check_box
        </span>
        <span style="text-decoration: line-through">Wait 0 seconds</span>`;
  }
  infoInnerHTMLGlob += `</div>`;
  if (tasksLeftGlob == 0) {
    infoInnerHTMLGlob = `
        <p style="margin-inline: 1em;">
            <b>Ready to Publish!</b>
        </p>
        <div style="margin-inline: 1em; margin-block-end: 1em;">
            ${infoInnerHTMLGlob}
        </div>`;
    editorPendingTasksTextEl.innerHTML = "Ready to publish";
    editorPendingTasksIconEl.innerHTML = "done";
    editorPendingTasksBtnEl.style.backgroundColor = "lightgreen";
    editorPendingTasksBtnEl.classList.add("stretch-zoom");
  } else {
    if (editorPendingTasksBtnEl.classList.contains("stretch-zoom"))
      editorPendingTasksBtnEl.classList.remove("stretch-zoom");
    infoInnerHTMLGlob = `
        <p style="margin-inline: 1em;">
            <b>Ready to Publish?</b>
        </p>
        <div style="margin-inline: 1em; margin-block-end: 1em;">
           ${infoInnerHTMLGlob}
        </div>`;
    editorPendingTasksBtnEl.style.backgroundColor = null;
    if (tasksLeftGlob == 1)
      editorPendingTasksTextEl.innerHTML = "1 task left.<br>Tap for info.";
    else if (tasksLeftGlob <= 3)
      editorPendingTasksTextEl.innerHTML = `${tasksLeftGlob} tasks left.<br>Tap for info.`;
    else throw new Error(`Invalid #tasks left: ${tasksLeftGlob}`);
  }
  if (!tasksLeftGlob) {
    editorSaveBtnEl.classList.remove("disabled-icons-container");
    editorSaveBtnEl.classList.add("stretch-zoom");
  } else if (!editorSaveBtnEl.classList.contains("disabled-icons-container")) {
    editorSaveBtnEl.classList.add("disabled-icons-container");
    editorSaveBtnEl.classList.remove("stretch-zoom");
  }
  if (gameState.cats && gameState.cats.num)
    editorPlayBtnEl.classList.remove("disabled-icons-container");
  else if (!editorPlayBtnEl.classList.contains("disabled-icons-container"))
    editorPlayBtnEl.classList.add("disabled-icons-container");
  if (
    levelDef.fieldSize &&
    (gameState.cats.num || Object.keys(gPlacements).length)
  )
    store.set("levelEditing", levelDef.copy());
  resetLangAttributeOfLeafs(editorPendingTasksTextEl);
  autoTranslate();

  if (__DEV__) console.log(JSON.stringify(levelDef));
}

addTouchEvent(editorSelectPendingTasksBtnEl, async () => {
  const btnElPrev = document.getElementById("info-publish-level-btn");
  if (btnElPrev && btnElPrev.parentNode)
    btnElPrev.parentNode.removeChild(btnElPrev);
  showInfo({
    position: POSITIONS.CENTER,
    innerHTML: infoInnerHTMLGlob,
    persist: true,
  });
  if (tasksLeftGlob) return;
  const btnEl = document.createElement("div");
  btnEl.id = "info-publish-level-btn";
  btnEl.classList.add("ripple", "button-on-white", "button-section");
  btnEl.style.color = "black";
  btnEl.style.backgroundColor = "#D3FF02";
  btnEl.style.margin = 0;
  btnEl.style.textAlign = "center";
  btnEl.textContent = "Publish game";
  infoEl.appendChild(btnEl);
  autoTranslateElements(btnEl);
  infoEl.onHide = () => btnEl.parentNode && btnEl.parentNode.removeChild(btnEl);
  addTouchEvent(btnEl, () => {
    hideInfo();
    publishLevelDialogEl.style.display = "initial";
  });
  await yieldToMain();
  initRipple();
});

let publicGlob = true;

addTouchEvent(publishLevelDialogOkEl, publish);
addTouchEvent(publishLevelDialogCancelEl, () => {
  publishLevelDialogEl.style.display = null;
});
addTouchEvent(publishLevelAccessControlRadioBtnEl, () => {
  publicGlob =
    !publishLevelAccessControlRadioBtnEl.querySelector(".checkbox").checked;
  if (publicGlob) {
    publishLevelAccessControlLabelPublicEl.classList.add("active");
    publishLevelAccessControlLabelPrivateEl.classList.remove("active");
  } else {
    publishLevelAccessControlLabelPublicEl.classList.remove("active");
    publishLevelAccessControlLabelPrivateEl.classList.add("active");
  }
});

addScrollContainerFocusEventCallback(
  publishLevelDescriptionTextareaEl,
  publishLevelDialogScrollContainerEl
);

let lvlDescriptionGlob = "";

publishLevelDescriptionTextareaEl.addEventListener("input", (event) => {
  lvlDescriptionGlob = event.target.value;
  checkInput();
});

async function checkInput() {
  const fields = [
    {
      element: publishLevelDescriptionTextareaEl,
      value: lvlDescriptionGlob,
      validate: (val) => val.length <= 400,
      characterLimit: 400,
    },
  ];
  const disable = await validateInput(fields);
  disablePublish(disable);
  return !disable;
}

const disablePublish = (disable) => {
  if (disable) {
    publishLevelDialogOkEl.setAttribute("disabled", "disabled");
    publishLevelDialogOkEl.style.backgroundColor = "#E0E0E0";
    publishLevelDialogOkEl.style.color = "#9E9E9E";
    publishLevelDialogOkEl.style.borderColor = "#9E9E9E";
    publishLevelDialogOkEl.classList.remove("button");
  } else {
    publishLevelDialogOkEl.removeAttribute("disabled");
    publishLevelDialogOkEl.style.backgroundColor = "#D3FF02";
    publishLevelDialogOkEl.style.color = "black";
    publishLevelDialogOkEl.style.borderColor = "black";
    publishLevelDialogOkEl.classList.add("button");
  }
};

function calculateWaitingTime() {
  const now = new Date();
  const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  let numLevelsPublishedInLastDay = 0;
  for (const date of store.last5PublishedLevel) {
    if (date > oneDayAgo) numLevelsPublishedInLastDay++;
  }
  if (numLevelsPublishedInLastDay < 5) return 0;
  else {
    const oldestDate = new Date(Math.min(...store.last5PublishedLevel));
    const waitingTime = oldestDate.getTime() - oneDayAgo.getTime();
    return waitingTime; // unit: ms
  }
}

function getWaitingTime() {
  const waitingTime = calculateWaitingTime();
  if (waitingTime) return prettyTimeDeltaFormat(waitingTime);
}

function setWaitingTimeInterval() {
  const waitingTime = getWaitingTime();
  const waitingTimeEl = document.getElementById("editor-waiting-time");
  if (waitingTimeEl) waitingTimeEl.textContent = waitingTime;
  if (waitingTime) {
    gTimeout = setTimeout(setWaitingTimeInterval, 1000);
  } else {
    handleEditorUpdate();
    clearTimeout(gTimeout);
    gTimeout = null;
    infoTextEl.innerHTML = infoInnerHTMLGlob;
  }
}

function updateLast5PublishedLevelDates() {
  store.last5PublishedLevel.push(new Date());
  store.last5PublishedLevel = store.last5PublishedLevel.slice(-5);
  store.save("last5PublishedLevel");
}
